import type { FC, ChangeEvent, KeyboardEvent } from 'react';
import { useId, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiData, StudioSearchBiEvent } from '@wix/da-bi/pkg/events';
import { Url } from '@wix/da-url';
import SearchInput from '@wix/da-ds/pkg/formControls/dsPresets/SearchInput';
import { StudioSection, StudioState } from '../../types/store';
import { useSelector } from 'react-redux';
import { getPlaygroundSubsection } from '../../selectors/studio';

import s from './StudioSearchBar.scss';

export interface Props {
  className?: string;
  initialValue?: string;
  activeSection: StudioSection;
  studioParams?: StudioState['params'];
}

const StudioSearchBar: FC<Props> = ({ className, initialValue, activeSection, studioParams }) => {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();
  const inputId = useId();
  const [value, updateValue] = useState(initialValue ?? '');
  const playgroundSection = useSelector(getPlaygroundSubsection);

  const onSearch = useCallback(
    (q: string) => {
      logBiEvent(
        BiData<StudioSearchBiEvent>({
          evid: 14,
          search_terms: q,
        })
      );
      window.location.assign(
        Url.studio({
          section: activeSection.toLowerCase() as any,
          playground: playgroundSection as any,
          searchQuery: q,
          folderId: studioParams?.subfolderId,
        })
      );
    },
    [activeSection, logBiEvent, playgroundSection, studioParams?.subfolderId]
  );

  const onChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => updateValue(ev.target.value),
    []
  );

  const onKeyUp = useCallback(
    (ev: KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Enter') {
        onSearch(value);
      }
    },
    [onSearch, value]
  );

  const onClearClick = useCallback(() => updateValue(''), []);

  return (
    <div className={cn(s['root'], className)}>
      <SearchInput
        id={inputId}
        wrapperClassName={s['search-input']}
        label={t('common.search')}
        placeholder={t('common.search')}
        value={value}
        onChange={onChange}
        onKeyUp={onKeyUp}
        onClearClick={onClearClick}
        isLabelHidden
        noBottomSpacing
      />
    </div>
  );
};
StudioSearchBar.displayName = 'StudioSearchBar';

export default StudioSearchBar;
